import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LANGS, LANG_META, DEFAULT_LANG, isLang } from "@/i18n/types";
import { localizePath, useLang } from "@/i18n/routing";
import { LANG_STORAGE_KEY } from "@/i18n/RootRedirect";
import { useDict } from "@/i18n/useDict";

const Flag = ({ code, className = "" }: { code: string; className?: string }) => (
  <span className={`fi fi-${code} rounded-sm ${className}`} style={{ width: "1.25rem", height: "0.9375rem" }} />
);

/** Strip any leading language segment, returning the default-language path. */
function basePathOf(pathname: string): string {
  const segs = pathname.split("/");
  if (isLang(segs[1]) && segs[1] !== DEFAULT_LANG) {
    const rest = "/" + segs.slice(2).join("/");
    return rest === "/" ? "/" : rest.replace(/\/$/, "");
  }
  return pathname;
}

type Props = { variant?: "desktop" | "mobile" };

const LanguageSwitcher = ({ variant = "desktop" }: Props) => {
  const lang = useLang();
  const { dict } = useDict();
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const choose = (target: string) => {
    setOpen(false);
    if (!isLang(target)) return;
    try {
      localStorage.setItem(LANG_STORAGE_KEY, target);
    } catch {
      /* ignore storage errors */
    }
    const base = basePathOf(pathname);
    navigate(localizePath(base, target) + search + hash);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        aria-label={dict.langSwitcher.label}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Flag code={LANG_META[lang].flag} />
        <span className="uppercase">{lang}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-3 w-44 rounded-xl border border-border bg-card shadow-xl p-1.5 ${
            variant === "mobile" ? "left-0" : "right-0"
          }`}
        >
          <ul className="space-y-0.5 max-h-80 overflow-auto">
            {LANGS.map((l) => (
              <li key={l}>
                <button
                  onClick={() => choose(l)}
                  className={`flex items-center gap-2.5 w-full text-left rounded-lg px-2.5 py-2 text-sm transition-colors ${
                    l === lang ? "bg-secondary text-foreground font-semibold" : "text-foreground hover:bg-muted"
                  }`}
                >
                  <Flag code={LANG_META[l].flag} />
                  <span>{LANG_META[l].name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
